import {
  InfiniteData,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'

import { RequestReturnType } from '@/api/models/notifications/lib/notifications.types'
import { notificationHelpersById } from '@/api/models/notifications/utils/notificationHelpersById'

import { NotificationsService } from '..'
import { useNotificationsCount } from './useNotificationsCount'
import { NOTIFICATIONS_LIST_RQ_KEY } from './useNotificationsList'
import { NOTIFICATIONS_LIST_INFINITE_QUERY_KEY } from './useNotificationsListInfinite'

export const useSetNotificationReadById = () => {
  const queryClient = useQueryClient()
  const { refetch } = useNotificationsCount()

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await NotificationsService.setNotificationReadById({ id }).then(() => {
        refetch()
      })
    },
    onMutate: async readNotification => {
      await queryClient.cancelQueries({
        queryKey: [NOTIFICATIONS_LIST_RQ_KEY, 1]
      })
      await queryClient.cancelQueries({
        queryKey: [NOTIFICATIONS_LIST_INFINITE_QUERY_KEY]
      })

      const previousNotificationData = queryClient.getQueryData([
        NOTIFICATIONS_LIST_RQ_KEY,
        1
      ])
      const previousInfiniteNotificationData = queryClient.getQueryData([
        NOTIFICATIONS_LIST_INFINITE_QUERY_KEY
      ])

      // Для случаев, если сработал инфинити скролл
      queryClient.setQueryData<
        InfiniteData<{ page: number; response: RequestReturnType }>
      >([NOTIFICATIONS_LIST_INFINITE_QUERY_KEY], old =>
        notificationHelpersById.updateInfiniteReadStatus(old, readNotification)
      )

      // // Для случаев, без инфинити скролла
      queryClient.setQueryData<RequestReturnType>(
        [NOTIFICATIONS_LIST_RQ_KEY, 1],
        old =>
          notificationHelpersById.updateFirstPageReadStatus(
            old,
            readNotification
          )
      )

      return {
        previousNotificationList: previousNotificationData,
        previousInfiniteNotificationList: previousInfiniteNotificationData
      }
    },
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(
        [NOTIFICATIONS_LIST_INFINITE_QUERY_KEY],
        context?.previousInfiniteNotificationList
      )
      queryClient.setQueryData(
        [NOTIFICATIONS_LIST_RQ_KEY, 1],
        context?.previousNotificationList
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_LIST_INFINITE_QUERY_KEY]
      })
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_LIST_RQ_KEY, 1]
      })
    }
  })
}
